import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldOff, Download, Search, 
  ChevronLeft, ChevronRight, Server, Users, HardDrive, Cpu, MemoryStick,
  Filter, X, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnlyARG } from '../../lib/utils';

const ExecutiveReport = () => {
  const [activeTab, setActiveTab] = useState('security');
  const [exporting, setExporting] = useState(false);

  // ============ SEGURIDAD ============
  const [securityStats, setSecurityStats] = useState({
    antivirus: { on: 0, off: 0, total: 0, percent: 0 },
    firewall: { on: 0, off: 0, total: 0, percent: 0 },
    encriptacion: { on: 0, off: 0, total: 0, percent: 0 },
    total_servers: 0
  });
  const [securityDetail, setSecurityDetail] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [securityFilter, setSecurityFilter] = useState({ tipo: '', estado: '', search: '' });
  const [securityLoading, setSecurityLoading] = useState(false);

  // ============ LOCAL ADMINS ============
  const [adminStats, setAdminStats] = useState({ total_entries: 0, total_servers: 0, unique_users: 0, non_standard_count: 0, top_users: [] });
  const [adminList, setAdminList] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [adminFilter, setAdminFilter] = useState({ search: '', server: '', user: '' });
  const [adminLoading, setAdminLoading] = useState(false);

  // ============ HARDWARE ============
  const [hardwareStats, setHardwareStats] = useState({ 
    total_servers: 0, total_cores: 0, total_ram_gb: 0, low_ram_count: 0,
    os_distribution: [], ram_distribution: []
  });
  const [hardwareList, setHardwareList] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [hardwareFilter, setHardwareFilter] = useState({ search: '', os_filter: '', ram_min: '', ram_max: '' });
  const [hardwareOptions, setHardwareOptions] = useState({ os_options: [] });
  const [hardwareLoading, setHardwareLoading] = useState(false);

  // ============ FETCH FUNCTIONS ============

  // Security
  const fetchSecurityStats = async () => {
    try {
      const response = await api.get('/reports/executive/security/stats');
      setSecurityStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSecurityDetail = async (page = 1) => {
    if (!securityFilter.tipo) return;
    setSecurityLoading(true);
    try {
      const params = new URLSearchParams({ tipo: securityFilter.tipo, page, page_size: 50 });
      if (securityFilter.estado) params.append('estado', securityFilter.estado);
      if (securityFilter.search) params.append('search', securityFilter.search);
      
      const response = await api.get(`/reports/executive/security/details?${params}`);
      setSecurityDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    } finally {
      setSecurityLoading(false);
    }
  };

  // Admins
  const fetchAdminStats = async () => {
    try {
      const response = await api.get('/reports/executive/localadmin/stats');
      setAdminStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAdminList = async (page = 1) => {
    setAdminLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (adminFilter.search) params.append('search', adminFilter.search);
      if (adminFilter.server) params.append('server', adminFilter.server);
      if (adminFilter.user) params.append('user', adminFilter.user);
      
      const response = await api.get(`/reports/executive/localadmin/list?${params}`);
      setAdminList(response.data);
    } catch (error) {
      toast.error('Error al cargar lista');
    } finally {
      setAdminLoading(false);
    }
  };

  // Hardware
  const fetchHardwareStats = async () => {
    try {
      const response = await api.get('/reports/executive/hardware/stats');
      setHardwareStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHardwareOptions = async () => {
    try {
      const response = await api.get('/reports/executive/hardware/filter-options');
      setHardwareOptions(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHardwareList = async (page = 1) => {
    setHardwareLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (hardwareFilter.search) params.append('search', hardwareFilter.search);
      if (hardwareFilter.os_filter) params.append('os_filter', hardwareFilter.os_filter);
      if (hardwareFilter.ram_min) params.append('ram_min', hardwareFilter.ram_min);
      if (hardwareFilter.ram_max) params.append('ram_max', hardwareFilter.ram_max);
      
      const response = await api.get(`/reports/executive/hardware/list?${params}`);
      setHardwareList(response.data);
    } catch (error) {
      toast.error('Error al cargar lista');
    } finally {
      setHardwareLoading(false);
    }
  };

  // Export
  const handleExport = async (type) => {
    setExporting(true);
    try {
      let endpoint = '';
      let filename = '';
      
      switch (type) {
        case 'security':
          endpoint = '/reports/executive/security/export';
          filename = 'seguridad_servidores';
          break;
        case 'admins':
          endpoint = '/reports/executive/localadmin/export';
          filename = 'admins_locales';
          break;
        case 'hardware':
          endpoint = '/reports/executive/hardware/export';
          filename = 'hardware_servidores';
          break;
        default:
          return;
      }
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Effects
  useEffect(() => {
    if (activeTab === 'security') {
      fetchSecurityStats();
    } else if (activeTab === 'admins') {
      fetchAdminStats();
      fetchAdminList(1);
    } else if (activeTab === 'hardware') {
      fetchHardwareStats();
      fetchHardwareOptions();
      fetchHardwareList(1);
    }
  }, [activeTab]);

  useEffect(() => {
    if (securityFilter.tipo) {
      fetchSecurityDetail(1);
    }
  }, [securityFilter]);

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdminList(1);
    }
  }, [adminFilter]);

  useEffect(() => {
    if (activeTab === 'hardware') {
      fetchHardwareList(1);
    }
  }, [hardwareFilter]);

  // Format date
  const formatDate = (dateStr) => formatDateOnlyARG(dateStr);

  // Get status color
  const getStatusColor = (percent) => {
    if (percent >= 90) return 'text-green-500';
    if (percent >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getProgressColor = (percent) => {
    if (percent >= 90) return 'bg-green-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Compliance Servidores</h1>
          <p className="page-subtitle">Dashboard de Seguridad, Administradores y Hardware de Servidores</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="h-4 w-4 mr-2" />
              Seguridad
            </TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins">
              <Users className="h-4 w-4 mr-2" />
              Admins Locales
            </TabsTrigger>
            <TabsTrigger value="hardware" data-testid="tab-hardware">
              <HardDrive className="h-4 w-4 mr-2" />
              Hardware
            </TabsTrigger>
          </TabsList>

          {/* ============ TAB: SEGURIDAD ============ */}
          <TabsContent value="security">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Antivirus */}
                <Card 
                  className={`cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${securityFilter.tipo === 'antivirus' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setSecurityFilter({ tipo: 'antivirus', estado: '', search: '' })}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-green-500" />
                        <span className="font-medium">Antivirus</span>
                      </div>
                      <span className={`text-2xl font-bold ${getStatusColor(securityStats.antivirus.percent)}`}>
                        {securityStats.antivirus.percent}%
                      </span>
                    </div>
                    <Progress value={securityStats.antivirus.percent} className="h-2 mb-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span className="text-green-500">{securityStats.antivirus.on} ON</span>
                      <span className="text-red-500">{securityStats.antivirus.off} OFF</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Firewall */}
                <Card 
                  className={`cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${securityFilter.tipo === 'firewall' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setSecurityFilter({ tipo: 'firewall', estado: '', search: '' })}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-yellow-500" />
                        <span className="font-medium">Firewall</span>
                      </div>
                      <span className={`text-2xl font-bold ${getStatusColor(securityStats.firewall.percent)}`}>
                        {securityStats.firewall.percent}%
                      </span>
                    </div>
                    <Progress value={securityStats.firewall.percent} className="h-2 mb-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span className="text-green-500">{securityStats.firewall.on} ON</span>
                      <span className="text-red-500">{securityStats.firewall.off} OFF</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Encriptación */}
                <Card 
                  className={`cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${securityFilter.tipo === 'encriptacion' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setSecurityFilter({ tipo: 'encriptacion', estado: '', search: '' })}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldOff className="h-5 w-5 text-red-500" />
                        <span className="font-medium">Encriptación</span>
                      </div>
                      <span className={`text-2xl font-bold ${getStatusColor(securityStats.encriptacion.percent)}`}>
                        {securityStats.encriptacion.percent}%
                      </span>
                    </div>
                    <Progress value={securityStats.encriptacion.percent} className="h-2 mb-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span className="text-green-500">{securityStats.encriptacion.on} ON</span>
                      <span className="text-red-500">{securityStats.encriptacion.off} OFF</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detail Table */}
              {securityFilter.tipo && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium capitalize">Detalle: {securityFilter.tipo}</h3>
                      <div className="flex items-center gap-2">
                        <select
                          value={securityFilter.estado}
                          onChange={(e) => setSecurityFilter(prev => ({ ...prev, estado: e.target.value }))}
                          className="h-9 text-sm bg-background border border-border rounded px-3"
                        >
                          <option value="">Todos</option>
                          <option value="on">Solo ON</option>
                          <option value="off">Solo OFF</option>
                        </select>
                        <Input
                          placeholder="Buscar servidor..."
                          value={securityFilter.search}
                          onChange={(e) => setSecurityFilter(prev => ({ ...prev, search: e.target.value }))}
                          className="h-9 w-48"
                        />
                        <Button variant="outline" size="sm" onClick={() => handleExport('security')} disabled={exporting}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {securityLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                      </div>
                    ) : (
                      <>
                        <table className="table-subtle">
                          <thead>
                            <tr>
                              <th>SERVIDOR</th>
                              <th>ESTADO</th>
                              <th>PRODUCTO</th>
                              <th>VERSIÓN</th>
                              <th>SITE</th>
                              <th>ACTUALIZACIÓN</th>
                            </tr>
                          </thead>
                          <tbody>
                            {securityDetail.items.map((item, idx) => (
                              <tr key={idx}>
                                <td className="font-mono">{item.computername}</td>
                                <td>
                                  {item.estado?.toUpperCase() === 'ON' ? (
                                    <span className="flex items-center gap-1 text-green-500"><CheckCircle className="h-4 w-4" /> ON</span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-red-500"><XCircle className="h-4 w-4" /> OFF</span>
                                  )}
                                </td>
                                <td>{item.nombre_producto || '-'}</td>
                                <td className="text-muted-foreground">{item.version || '-'}</td>
                                <td className="text-muted-foreground">{item.site_name || '-'}</td>
                                <td className="text-muted-foreground">{formatDate(item.last_update)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {securityDetail.pages > 1 && (
                          <div className="flex items-center justify-end gap-2 mt-4">
                            <Button variant="outline" size="sm" disabled={securityDetail.page === 1} onClick={() => fetchSecurityDetail(securityDetail.page - 1)}>
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm">{securityDetail.page} / {securityDetail.pages}</span>
                            <Button variant="outline" size="sm" disabled={securityDetail.page === securityDetail.pages} onClick={() => fetchSecurityDetail(securityDetail.page + 1)}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {!securityFilter.tipo && (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Haz click en una card para ver el detalle de servidores</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ TAB: ADMINS LOCALES ============ */}
          <TabsContent value="admins">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="stats-card flex items-center gap-3">
                  <Users className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value">{adminStats.total_entries}</div>
                    <div className="stats-card-label">Total Entradas</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <Server className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-blue-500">{adminStats.total_servers}</div>
                    <div className="stats-card-label">Servidores</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <Users className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-green-500">{adminStats.unique_users}</div>
                    <div className="stats-card-label">Usuarios Únicos</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-yellow-500">
                      {adminStats.non_standard_count}
                    </div>
                    <div className="stats-card-label">No Estándar</div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <Card className="card-subtle">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar..."
                      value={adminFilter.search}
                      onChange={(e) => setAdminFilter(prev => ({ ...prev, search: e.target.value }))}
                      className="h-9 w-48"
                    />
                    <Input
                      placeholder="Filtrar servidor..."
                      value={adminFilter.server}
                      onChange={(e) => setAdminFilter(prev => ({ ...prev, server: e.target.value }))}
                      className="h-9 w-48"
                    />
                    <Input
                      placeholder="Filtrar usuario..."
                      value={adminFilter.user}
                      onChange={(e) => setAdminFilter(prev => ({ ...prev, user: e.target.value }))}
                      className="h-9 w-48"
                    />
                    {(adminFilter.search || adminFilter.server || adminFilter.user) && (
                      <Button variant="ghost" size="sm" onClick={() => setAdminFilter({ search: '', server: '', user: '' })}>
                        <X className="h-4 w-4" /> Limpiar
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleExport('admins')} disabled={exporting} className="ml-auto">
                      <Download className="h-4 w-4 mr-2" /> Exportar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card>
                <CardContent className="p-0">
                  {adminLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                    </div>
                  ) : (
                    <>
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th>SERVIDOR</th>
                            <th>DOMINIO</th>
                            <th>USUARIO</th>
                            <th>SITE</th>
                            <th>ACTUALIZACIÓN</th>
                            <th>ESTADO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminList.items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="font-mono">{item.computer}</td>
                              <td className="text-muted-foreground">{item.domain}</td>
                              <td className="font-medium">{item.user}</td>
                              <td className="text-muted-foreground">{item.site_name || '-'}</td>
                              <td className="text-muted-foreground">{formatDate(item.last_update)}</td>
                              <td>
                                {item.is_standard ? (
                                  <span className="text-green-500 text-xs">Estándar</span>
                                ) : (
                                  <span className="flex items-center gap-1 text-yellow-500 text-xs">
                                    <AlertTriangle className="h-3 w-3" /> Extra
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {adminList.pages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t">
                          <span className="text-sm text-muted-foreground">{adminList.total} registros</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={adminList.page === 1} onClick={() => fetchAdminList(adminList.page - 1)}>
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm">{adminList.page} / {adminList.pages}</span>
                            <Button variant="outline" size="sm" disabled={adminList.page === adminList.pages} onClick={() => fetchAdminList(adminList.page + 1)}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============ TAB: HARDWARE ============ */}
          <TabsContent value="hardware">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="stats-card flex items-center gap-3">
                  <Server className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value">{hardwareStats.total_servers}</div>
                    <div className="stats-card-label">Servidores</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <Cpu className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-blue-500">{hardwareStats.total_cores}</div>
                    <div className="stats-card-label">Total Cores</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <MemoryStick className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-green-500">{hardwareStats.total_ram_gb} GB</div>
                    <div className="stats-card-label">Total RAM</div>
                  </div>
                </div>
                <div className="stats-card flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="stats-card-value text-red-500">{hardwareStats.low_ram_count}</div>
                    <div className="stats-card-label">RAM &lt; 4GB</div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <Card className="card-subtle">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar servidor/IP..."
                      value={hardwareFilter.search}
                      onChange={(e) => setHardwareFilter(prev => ({ ...prev, search: e.target.value }))}
                      className="h-9 w-48"
                    />
                    <select
                      value={hardwareFilter.os_filter}
                      onChange={(e) => setHardwareFilter(prev => ({ ...prev, os_filter: e.target.value }))}
                      className="h-9 text-sm bg-background border border-border rounded px-3"
                    >
                      <option value="">Todos los OS</option>
                      {hardwareOptions.os_options.map(os => (
                        <option key={os} value={os}>{os}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">RAM:</span>
                      <Input
                        type="number"
                        placeholder="Min GB"
                        value={hardwareFilter.ram_min}
                        onChange={(e) => setHardwareFilter(prev => ({ ...prev, ram_min: e.target.value }))}
                        className="h-9 w-24"
                      />
                      <span>-</span>
                      <Input
                        type="number"
                        placeholder="Max GB"
                        value={hardwareFilter.ram_max}
                        onChange={(e) => setHardwareFilter(prev => ({ ...prev, ram_max: e.target.value }))}
                        className="h-9 w-24"
                      />
                    </div>
                    {(hardwareFilter.search || hardwareFilter.os_filter || hardwareFilter.ram_min || hardwareFilter.ram_max) && (
                      <Button variant="ghost" size="sm" onClick={() => setHardwareFilter({ search: '', os_filter: '', ram_min: '', ram_max: '' })}>
                        <X className="h-4 w-4" /> Limpiar
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleExport('hardware')} disabled={exporting} className="ml-auto">
                      <Download className="h-4 w-4 mr-2" /> Exportar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card>
                <CardContent className="p-0">
                  {hardwareLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="table-subtle">
                          <thead>
                            <tr>
                              <th>SERVIDOR</th>
                              <th>IP</th>
                              <th>CORES</th>
                              <th>RAM</th>
                              <th>STORAGE</th>
                              <th>SISTEMA OPERATIVO</th>
                              <th>SITE</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hardwareList.items.map((item, idx) => (
                              <tr key={idx}>
                                <td className="font-mono">{item.hostname}</td>
                                <td className="text-muted-foreground font-mono">{item.ip}</td>
                                <td>{item.cores}</td>
                                <td className={parseFloat(item.ram) < 4 ? 'text-red-500 font-medium' : ''}>
                                  {item.ram}
                                </td>
                                <td>{item.storage}</td>
                                <td className="text-sm">{item.os}</td>
                                <td className="text-muted-foreground">{item.site_name || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {hardwareList.pages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t">
                          <span className="text-sm text-muted-foreground">{hardwareList.total} registros</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={hardwareList.page === 1} onClick={() => fetchHardwareList(hardwareList.page - 1)}>
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm">{hardwareList.page} / {hardwareList.pages}</span>
                            <Button variant="outline" size="sm" disabled={hardwareList.page === hardwareList.pages} onClick={() => fetchHardwareList(hardwareList.page + 1)}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ExecutiveReport;
